import './index.scss';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Button, Col, Row, Spin } from 'antd';
import LogoWhite from 'src/assets/svg/logo_white.svg?react';
import { useNavigate } from 'react-router-dom';
import { getRoles, J2LRole } from 'src/shared/utils/user';
import { useMount } from 'ahooks';

const HomeView: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = auth?.isAuthenticated === true;
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useMount(() => {
    if (process.env.NODE_ENV !== 'development') {
      if (isAuthenticated) redirectToApp();
      else window.location.href = window.REACT_APP_WEBSITE_BASE_URI;
    }
  });

  const redirectToApp = () => {
    if (isAuthenticated) {
      const roles = getRoles(auth?.user).filter((x) => x?.toString() !== 'User') ?? [];
      if ([J2LRole.Person, J2LRole.EnterpriseAdmin, J2LRole.EnterpriseUser].some((r) => roles.includes(r))) {
        setRedirecting(true);
        window.location.href = `${window.REACT_APP_WEBSITE_BASE_URI}/default-user-page`;
      } else if ([J2LRole.J2LAdmin, J2LRole.BackofficeUser].some((r) => roles.includes(r))) {
        setRedirecting(true);
        window.location.href = `${window.REACT_APP_BACKOFFICE_BASE_URI}`;
      } else {
        setRedirecting(false);
      }
    }
  };

  const authSpinState = (): [boolean, string] => {
    if (redirecting) {
      return [true, t('Redirigiendo')];
    }
    switch (auth.activeNavigator) {
      case 'signinSilent':
        return [true, t('Refrescando credenciales')];
      case 'signoutRedirect':
        return [true, t('Saliendo')];
    }

    if (auth.isLoading) {
      return [true, t('Autenticando')];
    }

    // Esto NO se puede descomentar tal cual: `authSpinState` devuelve `[boolean, string]` y el bloque
    // devuelve JSX. Y desde #145 hay ademas un cambio de forma que tener en cuenta al reescribirlo. Con
    // react-oidc-context 3, `auth.error` es un envoltorio que ya NO es `instanceof ErrorResponse` y
    // cuyo `.error` es `undefined`: el codigo OAuth vive en `innerError.error`, asi que quien
    // ramifique por codigo tiene que pasar por `oidcErrorCode` de `shared/configs/auth-config.ts`.
    // La forma exacta depende de por donde salga el fallo: los automaticos -`renewSilent`,
    // `signinCallback` y `signoutCallback`- pasan por `normalizeErrorFn` y llegan como
    // `{...normalizeError(error), source}`, SIN `args`; ese campo lo anade solo la ruta de los
    // metodos del navegador, que en esta aplicacion son `signinRedirect` y `signoutRedirect` desde
    // aqui, y `signinSilent` desde `shared/components/session-guard.tsx`. El `.message` en cambio NO
    // cambia: `normalizeError` copia el del error original cuando es string, y el generico de cada ruta
    // -«Renew silent failed» en la automatica, «Unknown error while executing ...» en la del
    // navegador- solo saldria con algo sin `message`, que oidc-client-ts no lanza. De donde sale ese
    // `.message`, leido en `node_modules/oidc-client-ts/dist/esm/oidc-client-ts.js` de la 3.5.0 (los
    // numeros de linea son
    // de ESE fichero, en `umd/` y `browser/` no cuadran): de un `ErrorResponse`, de
    // `super(error_description || error || '')`, o sea el texto que mande el servidor y, si no manda
    // ninguno, el codigo a secas («login_required»); de un `ErrorTimeout` del iframe, su literal
    // «IFrame timed out without a response» (linea 2547); de un corte de red, el mensaje del `fetch`
    // del navegador, porque la libreria relanza el error crudo con `throw err` y sus «Network Error» y
    // «Network error» son solo lineas de log (726 y 776). Y si en vez de fallar se cuelga, **no sale
    // ningun mensaje**: la libreria tiene un `ErrorTimeout('Network timed out')` (linea 700), pero
    // `fetchWithTimeout` sale antes con un `fetch` pelado cuando no hay `timeoutInSeconds` (687-688),
    // y esta SPA no fija `requestTimeoutInSeconds` en ningun sitio ni pide `offline_access`, asi que
    // el POST al token y el GET a userinfo se quedan colgados sin techo. Es lo mismo que ya dice
    // `shared/configs/auth-config.ts:312-316`, y la razon de que exista `OVERALL_CHECK_TIMEOUT_MS`.
    // if (auth.error) {
    //   return <div>Oops... {auth.error.message}</div>;
    // }
    return [false, null as unknown as string];
  };

  const [spin, spinMsg] = authSpinState();

  if (process.env.NODE_ENV !== 'development') return <Spin size="large" className="global-spinner" spinning={true} description={t('Redirigiendo')} />;

  return (
    <>
      {spin && <Spin size="large" className="global-spinner" spinning={spin} description={spinMsg} />}
      <div id="home-container">
        <div id="home-header">
          <div id="left-header">
            <Row gutter={[24, 16]} align="middle">
              <Col>
                <LogoWhite />
              </Col>
              <Col id="header-space" flex="auto"></Col>
              <Col span={5}>
                <Button className="menu-item" type="link">
                  {t('Para personas')}
                </Button>
              </Col>
              <Col span={5}>
                <Button className="menu-item" type="link">
                  {t('Para empresas')}
                </Button>
              </Col>
            </Row>
          </div>
          <div id="right-header">
            <Row gutter={[24, 16]}>
              {!isAuthenticated ? (
                <>
                  <Col className="menu-item">
                    <Button
                      className="big-btn"
                      id="login-btn"
                      onClick={() => auth?.signinRedirect()}
                      ghost
                      style={{ color: 'white' }}
                      size="large"
                      shape="round"
                    >
                      {t('Inicia sesión')}
                    </Button>
                  </Col>
                  <Col className="menu-item">
                    <Button
                      className="big-btn"
                      style={{ backgroundColor: 'white', color: '#29A098' }}
                      size="large"
                      shape="round"
                      onClick={() => navigate('/registration')}
                    >
                      {t('Únete')}
                    </Button>
                  </Col>
                </>
              ) : (
                <>
                  <Col flex={'auto'}>
                    <Button className="menu-item" type="link" onClick={redirectToApp}>
                      {t('Hola')} {auth.user?.profile.email}
                    </Button>
                  </Col>
                  <Col span={8} className="menu-item">
                    <Button
                      className="big-btn"
                      id="login-btn"
                      onClick={() => auth?.signoutRedirect()}
                      ghost
                      style={{ color: 'white' }}
                      size="large"
                      shape="round"
                    >
                      {t('Salir')}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>
        <div id="home-content">
          <p id="big-text">
            {t('El futuro es')} <span>{t('ahora')}</span>
          </p>
          <p id="medium-text">{t('Experimenta la libertad de elegir otra forma de trabajar')}</p>
          {!isAuthenticated ? (
            <Button
              onClick={() => navigate('/registration')}
              className="big-btn"
              style={{ height: 60, paddingLeft: 40, paddingRight: 40 }}
              shape="round"
              size="large"
              type="primary"
            >
              <span id="btn-text">{t('Únete a jobs2live')}</span>
            </Button>
          ) : (
            <Button
              onClick={redirectToApp}
              className="big-btn"
              style={{ height: 60, paddingLeft: 40, paddingRight: 40 }}
              shape="round"
              size="large"
              type="primary"
            >
              <span id="btn-text">{t('Comenzar')}</span>
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default HomeView;
